import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Accept, FileRejection } from "react-dropzone";
import { useQueryClient } from "@tanstack/react-query";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    TextArea,
} from "@jhool-io/fe-components";
import * as FilePicker from "../../../../../shared-ui/FilePicker/FilePicker";
import useToast from "../../../../../hooks/useToast";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../../utils/helpers";
import DeleteIcon from "../../../../../icons/Delete";
import {
    ICollectionsNote,
    INewCollectionsNote,
} from "../../../types/clients.types";
import {
    useAddCollectionsNoteDocuments,
    useDeleteCollectionsNoteDocuments,
    useEditCollectionsNote,
} from "../../../hooks/clients.mutations";

interface EditCollectionsNoteProps {
    collectionsNote: ICollectionsNote;
    onFormSubmit(): void;
}

const acceptedFileTypes: Accept = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/heic": [".heic"],
    "image/tiff": [".tif", ".tiff"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
    ],
};

const maxFileSizeInBytes = 10 * 1024 * 1024;

const schema = yup.object({
    description: yup.string().trim().required("Description is required"),
});

const uploadConditionText =
    "Maximum file size of 10MB: .pdf, .jpg, .png, .heic, .tiff, .docx";

export default function EditCollectionsNote({
    collectionsNote,
    onFormSubmit,
}: EditCollectionsNoteProps) {
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );
    const [documentToDeleteId, setDocumentToDeleteId] = React.useState<
        string | null
    >(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<INewCollectionsNote>({
        resolver: yupResolver(schema),
    });

    const editCollectionsNote = useEditCollectionsNote();
    const addDocuments = useAddCollectionsNoteDocuments();
    const deleteDocuments = useDeleteCollectionsNoteDocuments();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const invalidateCollectionsNotes = () => {
        queryClient.invalidateQueries({
            queryKey: [collectionsNote.client_id, "collections-notes"],
        });
    };

    const handleDrop = (acceptedFiles: File[]) => {
        setSelectedFiles((prev) =>
            prev ? [...prev, ...acceptedFiles] : [...acceptedFiles]
        );
    };

    const handleRejectedFiles = (fileRejections: FileRejection[]) => {
        const rejectedNames = fileRejections
            .map((rejection) => rejection.file.name)
            .join(", ");
        toast({
            mode: "error",
            message: `Could not attach ${rejectedNames}. ${uploadConditionText}`,
        });
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (!selectedFiles) return;
        const updated = selectedFiles.filter((file) => file.name !== filename);
        setSelectedFiles(updated.length > 0 ? updated : null);
    };

    const handleComplete = () => {
        invalidateCollectionsNotes();
        onFormSubmit();
    };

    const onSubmit = (noteData: INewCollectionsNote) => {
        editCollectionsNote.mutate(
            {
                collectionsNoteId: collectionsNote.collections_note_id,
                description: noteData.description,
            },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            "Collections note updated successfully",
                    });

                    if (!selectedFiles || selectedFiles.length === 0) {
                        handleComplete();
                        return;
                    }

                    const formData = new FormData();
                    selectedFiles.forEach((file) =>
                        formData.append("documents", file)
                    );

                    addDocuments.mutate(
                        {
                            collectionsNoteId:
                                collectionsNote.collections_note_id,
                            formData,
                        },
                        {
                            onSuccess: handleComplete,
                            onError: (err) => {
                                toast({
                                    mode: "error",
                                    message:
                                        err.response?.data.message ||
                                        "Could not add attachments to collections note",
                                });
                                handleComplete();
                            },
                        }
                    );
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update collections note at this time",
                    });
                },
            }
        );
    };

    const handleDeleteDocument = () => {
        if (!documentToDeleteId) return;

        deleteDocuments.mutate(
            {
                collectionsNoteId: collectionsNote.collections_note_id,
                documentIds: [documentToDeleteId],
            },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message || "Attachment deleted successfully",
                    });
                    invalidateCollectionsNotes();
                    setDocumentToDeleteId(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete attachment",
                    });
                },
            }
        );
    };

    return (
        <>
            <AlertDialog open={Boolean(documentToDeleteId)}>
                <AlertDialogContent
                    title="Delete attachment"
                    description="Are you sure you want to delete this attachment?"
                    saveText="Delete attachment"
                    cancelText="Cancel"
                    isDeleting
                    onSaveClick={handleDeleteDocument}
                    onCancelClick={() => setDocumentToDeleteId(null)}
                    isSaveBtnDisabled={deleteDocuments.isLoading}
                    isCancelBtnDisabled={deleteDocuments.isLoading}
                />
            </AlertDialog>
            <form id="edit-collections-note" onSubmit={handleSubmit(onSubmit)}>
                <div className="fg">
                    <TextArea
                        {...register("description")}
                        label="Description"
                        defaultValue={collectionsNote.description}
                        hasError={!!errors.description}
                        errorText={errors.description?.message}
                    />
                </div>
                <p className="font-medium !text-caption text-textSec flex items-center gap-x-1 mb-1">
                    Add attachments
                    <span
                        className="text-textSec text-small"
                        aria-label="optional"
                    >
                        (optional)
                    </span>
                </p>
                <FilePicker.FilePicker
                    onDrop={handleDrop}
                    accept={acceptedFileTypes}
                    maxSize={maxFileSizeInBytes}
                    onDropRejected={handleRejectedFiles}
                    disabled={
                        editCollectionsNote.isLoading || addDocuments.isLoading
                    }
                >
                    <FilePicker.FilePickerIcon />
                    <FilePicker.FilePickerMeta
                        description="Click to upload"
                        uploadConditionText={uploadConditionText}
                    />
                </FilePicker.FilePicker>
                <FilePicker.FilePickerSelectedFiles
                    selectedFiles={selectedFiles}
                    handleRemoveSelectedFile={handleRemoveSelectedFile}
                    wrapperClassName="mt-[--spacing-form-items]"
                />
                {collectionsNote.documents.length > 0 ? (
                    <div className="mt-[--spacing-form-items] flex flex-col gap-y-[--spacing-items]">
                        <p className="font-medium !text-caption text-textSec">
                            Current attachments
                        </p>
                        {collectionsNote.documents.map((doc) => (
                            <Card
                                key={doc.collections_note_document_id}
                                classNames="flex gap-x-1 py-2 justify-between items-center"
                            >
                                <div className="flex flex-col text-left">
                                    <span className="text-caption font-medium">
                                        {doc.document_name || "Attachment"}
                                    </span>
                                    <span className="text-caption text-textSec">
                                        {`${
                                            doc.uploaded_by
                                                ? displayNameInRightFormat(
                                                      {
                                                          firstName:
                                                              doc.uploaded_by
                                                                  .first_name,
                                                          lastName:
                                                              doc.uploaded_by
                                                                  .last_name,
                                                      },
                                                      false
                                                  )
                                                : "--"
                                        }, ${formatDate(doc.created_at)}`}
                                    </span>
                                </div>
                                <Button
                                    variant="can-hover"
                                    size="auto"
                                    aria-label="Delete attachment"
                                    onClick={() =>
                                        setDocumentToDeleteId(
                                            doc.collections_note_document_id
                                        )
                                    }
                                >
                                    <DeleteIcon />
                                </Button>
                            </Card>
                        ))}
                    </div>
                ) : null}
            </form>
        </>
    );
}
